import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery } from "@tanstack/react-query";
import { type ClientInferResponseBody } from "@ts-rest/core";

import { getAuth, isProfileAuth } from "~/providers/store/auth";
import { client } from "~/api/client";
import { type apiContract } from "~/api/contract";
import { DisplayError, DisplayErrorCodeEnum } from "~/utils/display-error";

export const reportsKeys = createQueryKeys("reports", {
  detail: ({ sessionId }: { sessionId: string }) => ({
    queryKey: [{ sessionId }]
  })
});

type ReportResponse = ClientInferResponseBody<
  typeof apiContract.v1.pos.reports.getById,
  200
>;

export type ReceiptReport = ReportResponse["receipt"];
export type SalesReport = ReportResponse["report"];

function useSalesReport(sessionId: string | undefined) {
  return useQuery({
    ...reportsKeys.detail({ sessionId: sessionId || "empty" }),
    queryFn: async () => {
      if (!sessionId) throw new Error("No session id provided");
      const auth = getAuth();
      if (!isProfileAuth(auth)) {
        throw new DisplayError(DisplayErrorCodeEnum.PROFILE_NOT_SELECTED);
      }

      const res = await client.v1.pos.reports.getById({
        params: { sessionId },
        query: { profileId: auth.profileId }
      });

      if (res.status === 200) {
        return res.body;
      }

      throw res;
    },
    enabled: Boolean(sessionId),
    staleTime: 1000 * 30 // 30 seconds
  });
}

function useSendEmailReport(sessionId: string) {
  return useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      const auth = getAuth();
      if (!isProfileAuth(auth)) {
        throw new DisplayError(DisplayErrorCodeEnum.PROFILE_NOT_SELECTED);
      }

      const res = await client.v1.pos.reports.email({
        params: { sessionId },
        body: { moduleId: auth.moduleId, profileId: auth.profileId, email }
      });

      if (res.status === 204) {
        return res.body;
      }

      throw res;
    }
  });
}

export { useSalesReport, useSendEmailReport };
